import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Alert, Box, Chip, FormControl, IconButton, InputLabel, MenuItem, Select,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import { SELECTION_COLUMN_ID } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { AddButton, RemoveButton } from "@plannel/components/ActionIconButton";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import Dialog from "@plannel/components/Dialog";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import Snackbar from "@plannel/components/Snackbar";
import TextFieldSearch from "@plannel/components/TextFieldSearch";
import scenarioService from "@plannel/services/ai/scenarioService";
import reduxUtil from "@plannel/utils/redux-util";
import { FS } from "@plannel/utils/font-util";
import TermAliasCell from "@plannel/pages/ai/term/TermAliasCell";
import { toneBadge, toneMain } from "@plannel/pages/ai/term/termColors";
import TermEditDialog from "@plannel/pages/ai/term/TermEditDialog";
import {
  LOCALES, LOCALE_LABELS, STATUS, addedAliases, customTone, definitionText, displayAliases, errorMessage, excludedAliases, rowStatus, searchText,
  termName, toPayload, uiLocale,
} from "@plannel/pages/ai/term/termUtil";
import dateUtils from "@plannel/utils/date-util";

// 관리자용 테넌트 용어 사전 — AI 가 알아들을 별칭(6개 언어)과 뜻(정의)을 표준 위에 차분으로 얹는다.
const FILTER_W = 150;
// 표준은 회색 배지, 변경·추가는 이 화면 전용 색(termColors) — 표준보다 한 단계 강하게
const STATUS_CHIP = {
  [STATUS.STANDARD]: { key: "aiTermStatusStandard", color: "default" },
  [STATUS.OVERRIDE]: { key: "aiTermStatusOverride", color: "default", tone: "override" },
  [STATUS.ADDED]: { key: "aiTermStatusAdded", color: "default", tone: "added" },
};
// 변경 구분 필터 = 배지와 같은 선택지("" 는 전체)
const STATUS_FILTERS = ["", STATUS.STANDARD, STATUS.OVERRIDE, STATUS.ADDED];

// 그리드 셀용 칩 — 행 높이(28px)에 맞춰 낮추고 세로 가운데 정렬한다(MUI small 칩 24px 는 셀 기준선에서 어긋난다)
const CellChips = ({ items, center = false }) => (
  <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", height: "100%", justifyContent: center ? "center" : "flex-start" }}>
    {items.map((c) => (
      <Chip key={c.key} size="small" color={c.color} label={c.label}
        sx={{ height: 18, fontSize: FS.CAPTION, "& .MuiChip-label": { px: 0.75 },
          ...(c.tone ? { bgcolor: toneBadge(c.tone), color: "common.white" } : {}) }} />
    ))}
  </Box>
);

// 행 동작 아이콘은 호버한 행에서만 보인다(자리는 유지해 배지가 흔들리지 않게 visibility 로)
const ROW_ACTION_CLASS = "term-row-action";
const ROW_ACTION_SX = {
  [`& .${ROW_ACTION_CLASS}`]: { visibility: "hidden" },
  // 선택(체크)한 행까지 켜면 체크한 수만큼 삭제 아이콘이 남아 목록이 시끄러워진다
  [`& .ag-row-hover .${ROW_ACTION_CLASS}`]: { visibility: "visible" },
};

// 체크할 수 있는 행 = 추가어(테넌트 고유 용어). 표준·변경 행은 행 아이콘으로만 되돌린다
const selectable = (row) => !!row && row.id != null && rowStatus(row) === STATUS.ADDED;

// 긴 문장 셀 — 말줄임 + 화면 표준 툴팁(MUI)으로 전문. custom 이면 강조 막대(변경=파랑·추가=초록), muted 면 흐린 글자(표준 그대로)
const TextCell = ({ text, custom = false, tone = "override", muted = false }) => (
  !text ? null : (
    <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
      {/* 막대는 셀 왼쪽 여백 안에 둬 본문 시작 위치가 다른 행과 같게 */}
      {custom && <Box sx={{ width: 3, height: "1.1em", flexShrink: 0, borderRadius: 1, bgcolor: toneMain(tone), ml: "-7px", mr: "4px" }} />}
      {/* 아래 행을 덮는 위치라 셀을 벗어나면 바로 닫는다 — 안 닫히면 아래 행 셀에 올릴 수 없다 */}
      <Tooltip title={<Box sx={{ whiteSpace: "pre-wrap" }}>{text}</Box>} placement="bottom-start" disableInteractive
        slotProps={{ tooltip: { sx: { maxWidth: 480 } } }}>
        <Box sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: muted ? "text.secondary" : "inherit" }}>{text}</Box>
      </Tooltip>
    </Box>
  )
);

const AiTermDefinition = ({ viewName }) => {
  const { t, i18n } = useTranslation();
  const reduxDispatch = useDispatch();
  // viewState 는 객체로 중첩 저장 — 최상위 숫자형 문자열은 복원 시 BigInt 로 바뀔 수 있다
  const saved = reduxUtil.getViewState(viewName);
  const [filters, setFilters] = useState(() => {
    const { showInternal, ...kept } = saved?.filters || {};
    const f = { status: "", q: "", locale: "", ...kept };
    // 예전 선택지(UNCHANGED 등)가 저장돼 있으면 전체로
    return STATUS_FILTERS.includes(f.status) ? f : { ...f, status: "" };
  });
  const search = useRef(filters.q || "");
  const gridRef = useRef();
  const [rows, setRows] = useState([]);
  const [tablesMissing, setTablesMissing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState({ open: false, row: null });
  const [confirm, setConfirm] = useState({ open: false, title: "", content: "", rows: [] });
  const [snack, setSnack] = useState({ open: false, severity: "", content: "" });
  const [saving, setSaving] = useState(false);
  const locale = filters.locale || uiLocale(i18n.language);

  const notify = (severity, content) => setSnack({ open: true, severity, content });

  const load = async () => {
    setLoading(true);
    try {
      const r = await scenarioService.termCatalog(uiLocale(i18n.language));
      setRows(r?.rows || []);
      setTablesMissing(!!r?.tables_missing);
    } catch {
      setRows([]);
      notify("error", t("agentTraceLoadFail"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  useEffect(() => {
    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(viewName, { filters })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  const visible = useMemo(() => {
    const q = (filters.q || "").trim().toLowerCase();
    return rows.filter((r) => {
      // 내부용 개념(지표·차원 이름표)은 관리 대상이 아니다
      if (r.row_kind === "CONCEPT" && !r.selectable) return false;
      const st = rowStatus(r);
      if (filters.status && st !== filters.status) return false;
      return !q || searchText(r).includes(q);
    });
  }, [rows, filters]);

  const save = async (row, draft) => {
    if (saving) return;                 // 저장 버튼 연타 — 두 번째 INSERT 는 유효 행 유일 제약에 걸린다
    setSaving(true);
    try {
      const r = await scenarioService.termSave([toPayload(row, draft)]);
      // 경고가 여러 개여도 스낵바는 하나라 이어 붙인다
      const warns = (r?.warnings || []).map((w) => t("aiTermOverlapWarning", { term: w.term, owner: w.owner }));
      if (warns.length) notify("warning", warns.join(" "));
      else notify("success", t("MSG_SaveSuccess"));
      setEditing({ open: false, row: null });
      await load();
    } catch (e) {
      notify("error", errorMessage(t, e));
    } finally {
      setSaving(false);
    }
  };

  const askRevert = (row) => setConfirm({
    open: true, rows: [row],
    title: row.row_kind === "CUSTOM" ? "delete" : "confirm",
    content: row.row_kind === "CUSTOM" ? t("MSG_DeleteConfirm") : t("aiTermRevertConfirm"),
  });

  // 툴바 삭제 — 체크한 추가어를 한 번에. 확인 문구는 표준 마스터 화면과 같게 둔다
  const askDeleteSelected = () => {
    const picked = gridRef.current?.api?.getSelectedRows() || [];
    if (!picked.length) {
      notify("warning", t("MSG_NoSelectedData"));
      return;
    }
    setConfirm({ open: true, rows: picked, title: "delete", content: t("MSG_DeleteConfirm") });
  };

  // 서버에서는 되돌리기도 삭제와 같은 논리 삭제다 — 문구만 행 종류로 가른다
  const removeRows = async (picked) => {
    try {
      await scenarioService.termDelete(picked.map((r) => String(r.id)));
      notify("success", picked.every((r) => r.row_kind === "CUSTOM") ? t("mktApprovalDeleteDone") : t("aiTermReverted"));
      setEditing({ open: false, row: null });
      await load();
    } catch (e) {
      notify("error", errorMessage(t, e));
    }
  };

  // 열 폭은 고정 — 화면 폭에 맞추지 않고 넘치면 가로 스크롤
  const columnDefs = useMemo(() => [
    // 배지 오른쪽에 행 호버 때만 동작 아이콘 — 변경: 표준으로 되돌리기 / 추가: 삭제. 편집은 행 더블클릭
    { colId: "status", headerName: t("aiTermColChangeType"), width: 116,
      valueGetter: (p) => t(STATUS_CHIP[rowStatus(p.data)].key),
      cellRenderer: (p) => {
        const c = STATUS_CHIP[rowStatus(p.data)];
        const custom = p.data.row_kind === "CUSTOM";
        return (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, height: "100%" }}>
            <CellChips items={[{ key: c.key, label: t(c.key), color: c.color, tone: c.tone }]} />
            {p.data.id == null ? <Box sx={{ width: 20 }} /> : (
              // 아래 행 버튼을 덮는 위치라 버튼을 벗어나면 바로 닫는다 — 안 닫히면 아래 행 버튼에 올릴 수 없다
              <Tooltip title={custom ? t("delete") : t("aiTermRevert")} disableInteractive>
                <IconButton className={ROW_ACTION_CLASS} size="small"
                  // 평소 회색, 버튼 호버 때만 빨강 + 옅은 빨강 바탕(행 호버 배경과 구분). 모달의 되돌리기/삭제와 같은 색
                  sx={{ p: 0.25, color: "text.secondary", "&:hover": { color: "error.main", bgcolor: (th) => alpha(th.palette.error.main, 0.12) } }}
                  onClick={() => askRevert(p.data)}>
                  {custom ? <DeleteOutlineIcon sx={{ fontSize: 16 }} /> : <RotateLeftIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      } },
    { colId: "term", headerName: t("aiTermColTerm"), width: 160,
      valueGetter: (p) => termName(p.data, locale) || p.data.term_cd },
    // 표시 언어의 유효 별칭(표준 − 제외 + 추가). 값은 복사·검색용 문자열, 화면은 태그 + +N
    { colId: "aliases", headerName: t("aiTermAliases"), width: 240,
      valueGetter: (p) => displayAliases(p.data, locale).join(" / "),
      cellRenderer: (p) => <TermAliasCell aliases={displayAliases(p.data, locale)} added={addedAliases(p.data, locale)}
        excluded={new Set(excludedAliases(p.data, locale).map((a) => a.toLowerCase()))} tone={customTone(p.data)}
        eGridCell={p.eGridCell} />,
      // Enter/Space 는 셀 안 목록 열기에 쓰고 그리드 기본 동작(행 선택 등)으로 넘기지 않는다
      suppressKeyboardEvent: (p) => p.event.key === "Enter" || p.event.key === " " },
    // 관리자 판단에 쓰지 않아 기본 숨김. 지원 대응용으로 열 메뉴에서 켤 수 있다
    { colId: "concept", headerName: t("aiTermColConceptId"), width: 200, hide: true,
      valueGetter: (p) => (p.data.row_kind === "CONCEPT" ? p.data.term_cd : ""),
      cellStyle: { fontFamily: "monospace" } },
    // 테넌트 정의 > 표준(glossary) 정의. 테넌트 정의는 강조 막대, 표준 그대로면 흐린 글자
    { colId: "meaning", headerName: t("aiTermDefinition"), width: 450,
      valueGetter: (p) => definitionText(p.data).text,
      cellRenderer: (p) => <TextCell text={p.value} custom={definitionText(p.data).custom} tone={customTone(p.data)}
        muted={!definitionText(p.data).custom} /> },
    // 보충 설명은 테넌트만 가진다(표준 glossary 에는 없음)
    { colId: "note", headerName: t("aiTermNote"), width: 320,
      valueGetter: (p) => p.data.override?.note_txt || "",
      cellRenderer: (p) => <TextCell text={p.value} custom tone={customTone(p.data)} /> },
    // 감사 열은 표준 마스터 화면(SiteMaster 등)과 같은 정의 — UTC(Z) 를 브라우저 시각으로, 사용자는 로그인 ID
    { headerName: t("createdTs"), field: "created_ts", width: 145, valueFormatter: dateUtils.formatDateTime },
    { headerName: t("createdBy"), field: "created_by_nm", colId: "createdBy", width: 120 },
    { headerName: t("updatedTs"), field: "updated_ts", width: 145, valueFormatter: dateUtils.formatDateTime },
    { headerName: t("updatedBy"), field: "updated_by_nm", colId: "updatedBy", width: 120 },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, locale]);

  const gridSetting = useMemo(() => {
    const g = DefaultGridSetting({ title: "menuAiTermDefinition", viewName, columnDefs });
    return { ...g, defaultColDef: { ...g.defaultColDef, editable: false },
      // 표준 마스터 화면과 같은 체크박스 삭제 — 지울 수 없는 행도 체크박스를 두되 비활성으로 그린다.
      // 매 렌더 새 객체로 주면 ag-grid 가 전 행에 isRowSelectable 을 다시 돌리므로 여기서 고정한다
      rowSelection: { ...g.rowSelection, headerCheckbox: true, checkboxes: true,
        // 이 화면은 편집이 행 더블클릭이라, 클릭 선택을 두면 읽으려고 연 행만 남고 체크가 날아간다
        enableClickSelection: false,
        isRowSelectable: (n) => selectable(n.data) } };
  }, [viewName, columnDefs]);

  const selectFilter = (key, label, options, render, width = FILTER_W, value = filters[key], onPick = (v) => setFilter(key, v)) => (
    <FormControl size="small" sx={{ width }}>
      <InputLabel shrink>{label}</InputLabel>
      <Select value={value} label={label} displayEmpty notched onChange={(e) => onPick(e.target.value)}>
        {options.map((o) => <MenuItem key={o || "all"} value={o}>{render(o)}</MenuItem>)}
      </Select>
    </FormControl>
  );

  return (
    <Box sx={{ height: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column" }}>
      <Box sx={{ px: 1.5, flexShrink: 0 }}>
        <FilterContainer sx={{ columnGap: 1 }}>
          {selectFilter("status", t("aiTermColChangeType"), STATUS_FILTERS,
            (o) => t(o ? STATUS_CHIP[o].key : "agentTraceAllModules"))}
          {/* UI 언어를 고르면 "" 로 저장해 이후 UI 언어 변경을 계속 따른다 */}
          {selectFilter("locale", t("aiTermDisplayLang"), LOCALES, (o) => LOCALE_LABELS[o], FILTER_W, locale,
            (v) => setFilter("locale", v === uiLocale(i18n.language) ? "" : v))}
          <TextFieldSearch sx={{ width: 220 }} label={t("aiTermSearchLabel")} searchCode={search}
            // 표준 화면과 같이 조회 = 서버에서 최신 목록을 다시 받고 그 위에서 거른다(별도 새로고침 버튼 없음)
            retrieve={() => { setFilter("q", search.current.trim()); load(); }} />
          {/* 동작 버튼은 표준 마스터 화면(SiteMaster)과 같이 마지막 자식 한 묶음 — 오른쪽 끝, 필터를 접어도 남는다 */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <AddButton label="aiTermAddEntry" disabled={tablesMissing}
              onClick={() => setEditing({ open: true, row: null })} />
            <RemoveButton disabled={tablesMissing} onClick={askDeleteSelected} />
          </Box>
        </FilterContainer>
      </Box>

      {tablesMissing && (
        <Alert severity="info" sx={{ fontSize: FS.BODY, py: 0, mx: 1.5, mb: 1, flexShrink: 0 }}>{t("aiTermTablesMissing")}</Alert>
      )}

      <Box className="ag-theme-balham" sx={{ flex: 1, minHeight: 240, mx: 1.5, mb: 1.5, ...ROW_ACTION_SX }}>
        <AgGridReact ref={gridRef} rowData={visible} columnDefs={columnDefs} {...gridSetting} loading={loading}
          getRowId={(p) => `${p.data.row_kind}:${p.data.term_cd}`}
          rowStyle={tablesMissing ? undefined : { cursor: "pointer" }}
          onCellDoubleClicked={(e) => {
            // 행 동작 아이콘·체크박스 위의 더블클릭까지 편집으로 번지지 않게 뺀다
            if (tablesMissing || e.column?.getColId?.() === SELECTION_COLUMN_ID
              || e.event?.target?.closest?.(`.${ROW_ACTION_CLASS}`)) return;
            setEditing({ open: true, row: e.data });
          }} />
      </Box>

      {editing.open && (
        <TermEditDialog open={editing.open} row={editing.row} locale={locale}
          onClose={() => setEditing({ open: false, row: null })} onSave={save} onRevert={askRevert} saving={saving} />
      )}
      <Dialog open={confirm.open} title={confirm.title} content={confirm.content}
        onClose={() => setConfirm({ open: false, title: "", content: "", rows: [] })}
        onHandler={(res) => { if (res) removeRows(confirm.rows); }} />
      <Snackbar open={snack.open} onClose={() => setSnack({ ...snack, open: false })}
        severity={snack.severity} content={snack.content} />
    </Box>
  );
};

export default AiTermDefinition;
